/**
 * How each GUEST card surface takes a card at one location: a hold the
 * pre-close pass captures, or a one-step sale. Configured by OrderUp staff on
 * Site Admin → Card payments (spec 2026-10-01-location-card-payments-design.md).
 *
 * Absent means SALE. A pay link is ALWAYS a sale: `public/pay.ts` posts the
 * Books receipt and marks the invoice paid at approval, so a hold that later
 * failed would leave a paid invoice with no money behind it.
 */
import type { IPaymentRoutingSettings } from '../payments/neutral/routing/PaymentRouting';
import type { PaymentProviderName, PaymentRail } from '../payments/neutral/enums/PaymentProvider';
import type { MerchantAccountChannel, MerchantAccountPublic } from './merchantAccounts';

export type GuestCardSurface = 'onlineCheckout' | 'payAtTable' | 'payLink';
export type GuestCardCaptureMode = 'hold' | 'sale';

export interface IGuestCardCapture {
  readonly onlineCheckout?: GuestCardCaptureMode;
  readonly payAtTable?: GuestCardCaptureMode;
  readonly payLink?: 'sale';
}

export function resolveGuestCardCapture(
  cfg: IGuestCardCapture | undefined,
  surface: GuestCardSurface,
): GuestCardCaptureMode {
  if (surface === 'payLink') return 'sale';
  return cfg?.[surface] === 'hold' ? 'hold' : 'sale';
}

/**
 * The location-document fields only the staff card-payments route may write.
 * `firestore-rules/00-legacy.rules` `cardPaymentFieldsUntouched()` carries the
 * same list; a parity test (Task 7) fails when the two disagree.
 */
export const CARD_PAYMENT_LOCKED_FIELDS = [
  'providerRouting',
  'paymentMode',
  'merchantId',
  'trxCredentials',
  'trxCredentialsPublic',
  'guestCardCapture',
  'cardPaymentsVersion',
  'merchantAccountRouting',
  'trxMerchantAccountIds',
] as const;

export type ProviderRoutingKey = keyof IPaymentRoutingSettings;

/**
 * Key-by-key merge. A whole-map replace is how merchant approval and the old
 * Site Admin modal erased fraud rules and per-source overrides.
 */
export function mergeProviderRouting(
  current: IPaymentRoutingSettings | undefined,
  patch: Partial<IPaymentRoutingSettings>,
  clearKeys: readonly ProviderRoutingKey[] = [],
): IPaymentRoutingSettings {
  const next: Record<string, unknown> = { ...(current ?? {}), ...patch };
  for (const key of clearKeys) delete next[key];
  return next as unknown as IPaymentRoutingSettings;
}

/**
 * Wire shapes of the staff card-payments route
 * (`apps/api/components/routes/admin/location-card-payments.ts`). The route's
 * `toView` is annotated with the view type, so the two cannot drift. No secret
 * is ever carried: a password or merchant id is reported only as `has…`.
 */
export interface LocationCardPaymentsView {
  /** `cardPaymentsVersion`; send back as `expectedVersion` on a change. */
  version: number;
  location: { tenantGuid: string; locationGuid: string; name: string };
  routing: {
    defaultProvider: PaymentProviderName | null;
    providerBySource: Record<string, PaymentProviderName>;
    providerByRail: Partial<Record<PaymentRail, PaymentProviderName>>;
    forceProvider: PaymentProviderName | null;
  };
  /** The provider an online card charge routes to today (all three surfaces charge as `online`). */
  routedProvider: Record<GuestCardSurface, PaymentProviderName | null>;
  /**
   * The PRIMARY account's identifiers, read from the sealed vault — the same
   * source every charge reads (`payment_credentials/{loc}`), never the
   * location's `trxCredentialsPublic` display copy, which can disagree (a
   * prod→dev mirror once copied production's display copy over development's).
   */
  credentials: {
    clientId: string | null;
    sourceId: string | null;
    payPageGuid: string | null;
    hasPassword: boolean;
  };
  /**
   * `vault` — `credentials` is what charges use. `unreadable` — the vault could
   * not be read, so `credentials` is the location's display copy, which may NOT
   * be what charges use; a credentials change is refused until it can be read.
   */
  credentialsSource: 'vault' | 'unreadable';
  /** The location's display copy names identifiers the vault does not hold. */
  credentialsDisplayCopyDiffers: boolean;
  mode: 'live' | 'sandbox';
  guestCardCapture: Record<GuestCardSurface, GuestCardCaptureMode>;
  holdsBrakeOn: boolean;
  fraud: {
    fraudPrevention: IPaymentRoutingSettings['fraudPrevention'] | null;
    enhancedDataEnabled: boolean;
    enhancedDataCommodityCode: string | null;
    boardedIndustry: IPaymentRoutingSettings['boardedIndustry'] | null;
  };
  approvedRails: { card: boolean; ach: boolean };
  /**
   * Additional TRX merchant accounts (MIDs). The primary account is
   * `credentials` above. `null` when the vault could not be read — the page
   * must not show an empty list as "none configured".
   */
  merchantAccounts: MerchantAccountPublic[] | null;
  /** The account each channel routes to (absent → `primary`). */
  merchantAccountRouting: Record<MerchantAccountChannel, string>;
  /** Saved cards may be charged on any of the location's accounts (one shared vault). */
  storedCardsShared: boolean;
  ownerSettings: {
    cardTipCollectionMode: string | null;
    batchOutTime: string | null;
    payAtTableEnabled: boolean;
  };
}

/** One audited change, newest first. `changes` holds only touched fields; secrets read "changed". */
export interface LocationCardPaymentsHistoryRow {
  id: string;
  version: number;
  /** The actor's email when recorded, else the actor id. */
  actor: string;
  /** ISO-8601 timestamp, as PostgREST returned it. */
  createdAt: string;
  changes: Record<string, { before: unknown; after: unknown }>;
}
